import { useEffect, useState } from 'react'
import { ALLOWED_FILE_TYPES } from '../../utils/documentUtils'

interface UseFileDragAndDropOptions {
  enabled: boolean
  onDropFile: (file: File) => void
}

interface UseFileDragAndDropResult {
  isDraggingFile: boolean
  isDraggingUnsupportedFile: boolean
}

/**
 * Listens on the window for files dragged in from outside the browser and
 * reports whether such a drag is currently in progress, invoking `onDropFile`
 * when one is dropped. `onDropFile` must be referentially stable (wrap it in
 * useCallback), otherwise the listeners re-attach on every render.
 */
export function useFileDragAndDrop({
  enabled,
  onDropFile,
}: UseFileDragAndDropOptions): UseFileDragAndDropResult {
  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const [isDraggingUnsupportedFile, setIsDraggingUnsupportedFile] = useState(false)

  useEffect(() => {
    if (!enabled) {
      return
    }

    // The browser fires dragenter/dragleave for every element the cursor
    // crosses, including nested children. Counting enter/leave pairs lets us
    // distinguish actually leaving the window from merely moving between
    // adjacent elements: a child's dragenter fires before its parent's
    // dragleave, so the depth never hits zero until the cursor exits entirely.
    let dragDepth = 0

    const containsFiles = (event: DragEvent): boolean =>
      event.dataTransfer?.types?.includes('Files') ?? false

    const handleDragEnter = (event: DragEvent) => {
      if (!containsFiles(event)) return
      event.preventDefault()
      dragDepth += 1
      setIsDraggingFile(true)
      // dataTransfer.items exposes MIME types during drag (before the drop), so
      // we can flag unsupported types early to give immediate visual feedback.
      const draggedFileTypes = Array.from(event.dataTransfer?.items ?? [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.type)
      const hasOnlySupportedTypes =
        draggedFileTypes.length > 0 &&
        draggedFileTypes.every((mimeType) => ALLOWED_FILE_TYPES.includes(mimeType))
      setIsDraggingUnsupportedFile(!hasOnlySupportedTypes)
    }

    const handleDragOver = (event: DragEvent) => {
      if (!containsFiles(event)) return
      // Without preventDefault the browser rejects the drop and navigates to
      // the file instead of letting us handle it.
      event.preventDefault()
    }

    const handleDragLeave = (event: DragEvent) => {
      if (!containsFiles(event)) return
      event.preventDefault()
      dragDepth -= 1
      if (dragDepth <= 0) {
        dragDepth = 0
        setIsDraggingFile(false)
        setIsDraggingUnsupportedFile(false)
      }
    }

    const handleDrop = (event: DragEvent) => {
      event.preventDefault()
      dragDepth = 0
      setIsDraggingFile(false)
      setIsDraggingUnsupportedFile(false)
      const file = event.dataTransfer?.files?.[0]
      if (file) {
        onDropFile(file)
      }
    }

    window.addEventListener('dragenter', handleDragEnter)
    window.addEventListener('dragover', handleDragOver)
    window.addEventListener('dragleave', handleDragLeave)
    window.addEventListener('drop', handleDrop)

    return () => {
      window.removeEventListener('dragenter', handleDragEnter)
      window.removeEventListener('dragover', handleDragOver)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('drop', handleDrop)
    }
  }, [enabled, onDropFile])

  return { isDraggingFile, isDraggingUnsupportedFile }
}
